import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { Target, ArrowRight } from 'lucide-react';

const GoalPlanningPage = () => {
  const calculators = [
    {
      title: 'Goal Planner',
      description: 'Plan any financial goal with precision',
      features: ['Set custom target amounts', 'Define your timeline', 'Get exact SIP required'],
      icon: '🎯',
      link: '/calculators/goal-planner',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Marriage Planning',
      description: 'Plan your dream wedding without stress',
      features: ['Wedding budget estimation', 'Inflation-adjusted costs', 'Smart investment strategy'],
      icon: '💍',
      link: '/calculators/marriage',
      gradient: 'from-rose-500 to-pink-500'
    },
    {
      title: 'Education Planning',
      description: 'Secure your child\'s future education',
      features: ['India & abroad options', '10% education inflation', 'Age-based planning'],
      icon: '🎓',
      link: '/calculators/education',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'House Planning',
      description: 'Build your dream home fund',
      features: ['Down payment calculator', 'Property appreciation', 'Registration costs included'],
      icon: '🏠',
      link: '/calculators/house',
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Emergency Fund',
      description: 'Build your financial safety net',
      features: ['3-12 months coverage', 'Expense breakdown', 'Investment recommendations'],
      icon: '🛡️',
      link: '/calculators/emergency',
      gradient: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Goal Planning Calculators
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Achieve life goals with our comprehensive interactive tools. Plan for retirement, education, home, emergency funds, and more.
            </p>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-6">Why Should You Have a Goal Planner?</h2>
            <p className="text-white/70 mb-8">
              Big or small, everyone has life goals — some close, some far away. Whether it&apos;s building a fund for your child&apos;s education, planning a dream wedding, or preparing for a comfortable retirement, goals give structure to your finances and direction to your life.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">🎯 Clarity On What You&apos;re Saving For</h3>
                <p className="text-white/70">Define specific financial goals, set realistic targets, and track progress over time.</p>
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">📈 Stay Ahead of Inflation</h3>
                <p className="text-white/70">Ensure your savings stay relevant with proper asset allocation and periodic reviews.</p>
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">🛡 Peace of Mind</h3>
                <p className="text-white/70">Be financially ready for life&apos;s big moments without scrambling or borrowing.</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {calculators.map((calc, index) => (
              <Link
                key={index}
                to={calc.link}
                className="group bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:scale-[1.02]"
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r ${calc.gradient} rounded-xl mb-4 text-2xl`}>
                  {calc.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{calc.title}</h3>
                <p className="text-white/60 mb-4">{calc.description}</p>
                <ul className="space-y-2 mb-6">
                  {calc.features.map((feature, idx) => (
                    <li key={idx} className="text-white/70 flex items-start gap-2 text-sm">
                      <span className="text-white mt-0.5">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center text-white group-hover:text-white/80 transition-colors">
                  <span className="font-semibold">Calculate Now</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/10 inline-block">
              <h3 className="text-2xl font-bold text-white mb-4">Need Help Choosing?</h3>
              <p className="text-white/70 mb-6 max-w-lg">
                Our financial experts can help you prioritize your goals and create a personalized investment plan.
              </p>
              <Link
                to="/contact"
                className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300"
              >
                Talk to an Expert
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GoalPlanningPage;
